// A day picked in a panel next to what opened it (a due date, the day to do
// something). With a mouse it is the month calendar (calendar.tsx); on a touch
// screen it is the native date field, whose own picker the phone draws better.
//
// Promise:
// - Picking a day closes the panel, hands the focus back to the anchor and
//   calls `onChange` with "YYYY-MM-DD" (only when it changed).
// - `clearLabel` shows a button while there is a value; it closes the panel and
//   calls `onChange("")`.
// - The calendar takes the keys (arrows, PageUp / PageDown, Home / End, Enter);
//   Escape, a press outside or Tab out of the panel closes it unchanged.
// - On a touch screen the field takes the focus when the panel opens, and a
//   day chosen in it closes the panel at once.
import {
  CalendarPopover,
  PICKER_FOOTER_BUTTON,
  type PickerPopoverProps,
} from "./calendar";
import { useCoarsePointer } from "./pointer";
import { Popover } from "./popover";

export function DatePopover(props: PickerPopoverProps) {
  const coarse = useCoarsePointer();
  if (!coarse) return <CalendarPopover {...props} />;
  return <NativeDatePopover {...props} />;
}

function NativeDatePopover({
  open,
  anchor,
  onClose,
  label,
  align,
  value,
  min,
  max,
  clearLabel,
  onChange,
}: PickerPopoverProps) {
  const finish = (next: string) => {
    onClose();
    anchor.current?.focus({ preventScroll: true });
    if (next !== value) onChange(next);
  };
  return (
    <Popover
      anchor={anchor}
      open={open}
      onClose={onClose}
      label={label}
      align={align}
      className="min-w-0!"
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <input
          type="date"
          aria-label={label}
          // biome-ignore lint/a11y/noAutofocus: opened to pick a day
          autoFocus
          defaultValue={value}
          min={min}
          max={max}
          onChange={(e) => {
            if (e.target.value) finish(e.target.value);
          }}
          className="plain-field rounded-[7px] border border-field bg-surface px-[10px] py-[6px] text-[13px] text-fg outline-none focus:border-field-strong"
        />
        {clearLabel && value && (
          <button
            type="button"
            onClick={() => finish("")}
            className={`${PICKER_FOOTER_BUTTON} whitespace-nowrap`}
          >
            {clearLabel}
          </button>
        )}
      </div>
    </Popover>
  );
}
